import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { Grid } from "@mui/material";
import { ProjectEdit } from "../../../components/dashboard/projects/project-edit";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { mapErrors } from "../../../components/Utils";
import { fetchProject } from "../../../api/project";
import { useProjectPermissions } from "../../../routes/roleOnly";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

export const ProjectEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    project: {},
  });

  const { isLoading, error, errors, project: item } = itemState;

  const projectPermissions = useProjectPermissions(item);


  useEffect(() => {
    if (id) {
      fetchProjectData();
    }
  }, [id]);

  const fetchProjectData = async () => {
    try {
      setItemState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: false,
        errors: [],
      }));

      const response = await fetchProject(id, { fields: "id,name,description,project_number,start_date,end_date,active,finished,participants,activity_items,customer,delivery_address,project_manager,contact,project_category,department,status" });
      setItemState((prevState) => ({
        ...prevState,
        isLoading: false,
        project: response.data,
      }));
    } catch (err) {
      console.error("Error fetching project:", err);
      const errors = mapErrors(err);
      setItemState((prevState) => ({
        ...prevState,
        isLoading: false,
        error: true,
        errors: errors,
      }));
    }
  };

  const handleOnRefresh = (updatedProject) => {
    setItemState((prevState) => ({
      ...prevState,
      project: updatedProject,
    }));
  };

  const handleBackToProject = () => {
    navigate(`/dashboard/projects/${id}`);
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  if (error) {
    return <ResourceError errors={errors} />;
  }

  if (!projectPermissions.isAdmin) {
    return (
      <DashboardHeader
        title="Access Denied"
        subtitle="You don't have permission to edit this project."
        backButton={{
          label: "Back to Project",
          path: `/dashboard/projects/${id}`,
          icon: ArrowLeftIcon,
        }}
      >
        <div className="text-center">
          <button
            onClick={handleBackToProject}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back to Project
          </button>
        </div>
      </DashboardHeader>
    );
  }

  return (
    <DashboardHeader
      title={`${t("project.edit")}: ${item?.name || "Untitled Project"}`}
      subtitle={`Project Number: ${item?.project_number || "No number"}`}
      backButton={{
        label: "Back to Project",
        path: `/dashboard/projects/${id}`,
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
            <ProjectEdit item={item} onRefresh={handleOnRefresh} />
          </div>
        </div>
      </div>
    </DashboardHeader>
  );
};
